/**
 * src/domain/inventory/ProductProjection.ts
 *
 * Tier 3 Centralized Privacy Interceptor & Data Projection Engine.
 * Enforces role- and profile-gated redaction rules (price masking, provenance sanitization).
 */

import { DomainProduct, ArtworkProduct } from './types/product-types';
import { ProfileConfig } from '../../config/profiles/types';
import { publicPortalConfig } from '../../config/profiles/public-portal.config';

export class ProductProjection {
  public static projectProductForProfile(
    product: DomainProduct,
    profile: ProfileConfig = publicPortalConfig
  ): DomainProduct {
    if (!product) return product;

    // Clone base
    const projected: DomainProduct = JSON.parse(JSON.stringify(product));

    // 1. Price privacy enforcement
    if (!profile.features.showPrices && projected.type === 'artwork') {
      projected.price = {
        isPrivate: true,
        amount: null,
        currency: projected.price.currency,
        formatted: 'Price on Request',
      };
    }

    // 2. Public Portal specific redactions
    if (profile.id === 'public-portal' && projected.type === 'artwork') {
      const art = projected as ArtworkProduct;

      // Redact internal vault room details from public eye
      if (art.metadata.location) {
        art.metadata.location = {
          site: art.metadata.location.site || 'Exhibition Gallery',
          // room, row, shelf stripped
        };
      }

      // Redact private internal notes from provenance timeline
      if (art.metadata.provenance && Array.isArray(art.metadata.provenance)) {
        art.metadata.provenance = art.metadata.provenance.map((rec) => ({
          ...rec,
          notes: rec.event === 'creation' ? 'Registered on Sovereign Fine Art Mesh Ledger' : 'Verified Provenance Record',
        }));
      }

      // If COA view is not allowed on public portal, mask signature details
      if (!profile.features.allowCertificateView && art.metadata.certificateOfAuthenticity) {
        art.metadata.certificateOfAuthenticity = {
          ...art.metadata.certificateOfAuthenticity,
          ed25519Signature: 'REDACTED_PUBLIC_VIEW',
          sha256Digest: 'REDACTED_PUBLIC_VIEW',
        };
      }
    }

    return projected;
  }
}
